import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card,
  Button,
  Typography,
  Space,
  List,
  Tag,
  Empty,
  Spin,
  Divider,
  Row,
  Col,
  message,
} from 'antd';
import {
  FileTextOutlined,
  ArrowLeftOutlined,
  CarOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import PublicLayout from '../components/landing/PublicLayout';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import { customerPortalApi } from '../services/apiInstances';
import { feesFromStored } from '../utils/estimateTotals';

const { Title, Text } = Typography;

const statusColor = (status?: string): string => {
  switch ((status || '').toLowerCase()) {
    case 'approved':
    case 'completed':
      return 'green';
    case 'declined':
    case 'rejected':
      return 'red';
    case 'sent':
    case 'pending':
      return 'blue';
    default:
      return 'default';
  }
};

const money = (value: any): string => {
  const n = Number(value);
  return `$${(Number.isFinite(n) ? n : 0).toFixed(2)}`;
};

interface LineItem {
  id: number;
  // Legacy estimate schema
  category?: string;
  description?: string;
  quantity?: number;
  labor_hours?: number | null;
  hourly_rate?: number | null;
  total_price?: number;
  // 3C schema (defensive fallbacks)
  concern?: string;
  line_total?: number;
  approval_status?: string;
}

const CustomerEstimateDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { isAuthenticated, accessToken } = useCustomerAuth();
  const [loading, setLoading] = useState(true);
  const [estimate, setEstimate] = useState<any>(null);
  const [lineItems, setLineItems] = useState<LineItem[]>([]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/customer/login');
      return;
    }
    const load = async () => {
      setLoading(true);
      try {
        const res = await customerPortalApi.get(`/customer-portal/estimates/${id}`);
        setEstimate(res.data.estimate || null);
        setLineItems(res.data.lineItems || []);
      } catch (error) {
        message.error('Could not load this estimate.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isAuthenticated, navigate, accessToken, id]);

  if (loading) {
    return (
      <PublicLayout>
        <div style={{ textAlign: 'center', padding: '100px 20px' }}>
          <Spin size="large" />
        </div>
      </PublicLayout>
    );
  }

  const lineTitle = (item: LineItem) =>
    item.description || item.concern || item.category || 'Service item';
  const lineAmount = (item: LineItem) =>
    item.total_price != null ? item.total_price : item.line_total;

  return (
    <PublicLayout>
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #f0f2f5 0%, #ffffff 100%)',
        padding: '40px 20px'
      }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          {/* Header */}
          <Card style={{ marginBottom: 24 }}>
            <Space direction="vertical" size={0} style={{ width: '100%' }}>
              <Button
                type="link"
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate('/customer/estimates')}
                style={{ padding: 0, marginBottom: 8 }}
              >
                Back to Estimates
              </Button>
              {!estimate ? null : (
                <Row align="middle" justify="space-between" gutter={[8, 8]}>
                  <Col>
                    <Title level={3} style={{ margin: 0 }}>
                      <FileTextOutlined /> Estimate {estimate.estimate_number}
                    </Title>
                    <Text type="secondary">
                      <CarOutlined /> {estimate.year} {estimate.make} {estimate.model}
                    </Text>
                  </Col>
                  <Col>
                    <Tag color={statusColor(estimate.status)} style={{ fontSize: 14 }}>
                      {(estimate.status || 'unknown').toUpperCase()}
                    </Tag>
                  </Col>
                </Row>
              )}
            </Space>
          </Card>

          {!estimate ? (
            <Card>
              <Empty description="Estimate not found" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            </Card>
          ) : (
            <>
              {/* Line items */}
              <Card title="Line Items" style={{ marginBottom: 24 }}>
                {lineItems.length > 0 ? (
                  <List
                    dataSource={lineItems}
                    renderItem={(item) => (
                      <List.Item>
                        <List.Item.Meta
                          title={<Text strong>{lineTitle(item)}</Text>}
                          description={
                            <Space size="small" wrap>
                              {item.category && <Tag>{item.category}</Tag>}
                              {item.quantity != null && (
                                <Text type="secondary">Qty: {item.quantity}</Text>
                              )}
                              {item.labor_hours != null && (
                                <Text type="secondary">{item.labor_hours} hrs</Text>
                              )}
                            </Space>
                          }
                        />
                        <Text strong>{money(lineAmount(item))}</Text>
                      </List.Item>
                    )}
                  />
                ) : (
                  <Empty description="No line items" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                )}
              </Card>

              {/* Totals */}
              <Card title="Summary">
                <Space direction="vertical" style={{ width: '100%' }} size="small">
                  <Row justify="space-between">
                    <Text type="secondary">Subtotal</Text>
                    <Text>{money(estimate.subtotal)}</Text>
                  </Row>
                  {feesFromStored({
                    subtotal: estimate.subtotal,
                    discount_amount: estimate.discount_amount,
                    // Detail route serves e.* (tax_amount); list route aliases it to `tax`.
                    tax_amount: estimate.tax_amount ?? estimate.tax,
                    total_amount: estimate.total_amount,
                  }) > 0 && (
                    <Row justify="space-between">
                      <Text type="secondary">Fees</Text>
                      <Text>{money(feesFromStored({
                        subtotal: estimate.subtotal,
                        discount_amount: estimate.discount_amount,
                        tax_amount: estimate.tax_amount ?? estimate.tax,
                        total_amount: estimate.total_amount,
                      }))}</Text>
                    </Row>
                  )}
                  <Row justify="space-between">
                    <Text type="secondary">Tax</Text>
                    <Text>{money(estimate.tax_amount ?? estimate.tax)}</Text>
                  </Row>
                  <Divider style={{ margin: '8px 0' }} />
                  <Row justify="space-between">
                    <Text strong style={{ fontSize: 18 }}>Total</Text>
                    <Text strong style={{ fontSize: 18 }}>{money(estimate.total_amount)}</Text>
                  </Row>
                  {estimate.created_at && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Created {dayjs(estimate.created_at).format('MMM D, YYYY')}
                    </Text>
                  )}
                </Space>
              </Card>
            </>
          )}
        </div>
      </div>
    </PublicLayout>
  );
};

export default CustomerEstimateDetail;
